import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { apiJson, apiClient } from "@/lib/api";

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string, name: string, referralCode?: string) => Promise<User>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signOut: async () => {},
  login: async () => { throw new Error("Not implemented"); },
  register: async () => { throw new Error("Not implemented"); },
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const data = await apiJson<User>("/api/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<User> => {
    const data = await apiJson<User>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    setUser(data);
    return data;
  };

  const register = async (email: string, password: string, name: string, referralCode?: string): Promise<User> => {
    const body: Record<string, string> = { email, password, name };
    if (referralCode) body.referral_code = referralCode;
    const data = await apiJson<User>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify(body),
    });
    setUser(data);
    return data;
  };

  const signOut = async () => {
    try {
      await apiClient("/api/auth/logout", { method: "POST" });
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signOut, login, register }}>
      {children}
    </AuthContext.Provider>
  );
};
